/**
 * Change Plan Modal Component - Unified Admin Module
 * 
 * Reusable modal for changing user plans with custom limits support.
 * Used across admin dashboard and user detail views for consistency.
 */

import React, { useState, useEffect } from 'react';
import { useAdminData } from '../hooks/useAdminData';
import { getPlanInfo } from '../utils';
import type { PlanType, UserIdentifier } from '../types';

interface ChangePlanModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  userIdentifier: UserIdentifier;
  currentPlan?: PlanType;
  currentWeeklyAllowance?: number;
  currentMonthlyAllowance?: number;
  userDisplayName?: string;
}

/**
 * Unified change plan modal with custom limits support
 */
export const ChangePlanModal: React.FC<ChangePlanModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  userIdentifier,
  currentPlan = 'starter',
  currentWeeklyAllowance,
  currentMonthlyAllowance,
  userDisplayName
}) => {
  const { operations, billingConfig } = useAdminData();
  
  // Form state
  const [planForm, setPlanForm] = useState({
    planType: 'starter' as PlanType,
    monthlyLimit: ''
  });

  // Load billing configuration when modal opens
  useEffect(() => {
    if (isOpen) {
      billingConfig.loadConfig();
      // Reset form when modal opens
      setPlanForm({
        planType: 'starter' as PlanType,
        monthlyLimit: ''
      });
    }
  }, [isOpen]);

  // Update form when plan type changes to pre-populate current limits
  useEffect(() => {
    if (planForm.planType === 'monthly' && planForm.monthlyLimit === '' && billingConfig.config) {
      const monthlyValue = currentMonthlyAllowance !== undefined 
        ? currentMonthlyAllowance.toString()
        : billingConfig.config.default_monthly_allowance.toString();
      
      setPlanForm(prev => ({
        ...prev,
        monthlyLimit: monthlyValue
      }));
    }
  }, [planForm.planType, planForm.monthlyLimit, currentMonthlyAllowance, billingConfig.config]);

  const handleChangePlan = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!userIdentifier) return;

    // Parse limits for monthly plans
    let monthlyLimit: number | undefined;
    
    if (planForm.planType === 'monthly' && planForm.monthlyLimit) {
      monthlyLimit = parseFloat(planForm.monthlyLimit);
      if (isNaN(monthlyLimit) || monthlyLimit < 0) {
        alert('Please enter a valid monthly limit');
        return;
      }
    }

    const success = await operations.changePlan(
      userIdentifier, 
      planForm.planType, 
      undefined,  // weeklyLimit removed
      monthlyLimit
    );

    if (success) {
      setPlanForm({
        planType: 'starter' as PlanType,
        monthlyLimit: ''
      });
      onSuccess();
      onClose();
    }
  };

  const handleClose = () => {
    setPlanForm({
      planType: 'starter' as PlanType,
      monthlyLimit: ''
    });
    operations.clearError();
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-5 border w-[500px] shadow-lg rounded-md bg-white">
        <div className="mt-3">
          <h3 className="text-lg font-medium text-gray-900 mb-4">
            Change Plan{userDisplayName && ` for ${userDisplayName}`}
          </h3>
          
          <form onSubmit={handleChangePlan} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                New Plan Type
              </label>
              <select
                value={planForm.planType}
                onChange={(e) => {
                  const newPlanType = e.target.value as PlanType;
                  
                  // Pre-populate with current limits for monthly plans
                  if (newPlanType === 'monthly') {
                    const monthlyValue = currentMonthlyAllowance !== undefined 
                      ? currentMonthlyAllowance.toString()
                      : (billingConfig.config?.default_monthly_allowance?.toString() || '50.00');
                    
                    setPlanForm(prev => ({ 
                      ...prev, 
                      planType: newPlanType,
                      monthlyLimit: monthlyValue
                    }));
                  } else {
                    // Reset for other plan types
                    setPlanForm(prev => ({ 
                      ...prev, 
                      planType: newPlanType,
                      monthlyLimit: ''
                    }));
                  }
                }}
                className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                autoFocus
              >
                <option value="starter">Starter Plan</option>
                <option value="monthly">Monthly Plan</option>
                <option value="unlimited">Unlimited Plan</option>
              </select>
            </div>

            {/* Limit Fields Section */}
            {planForm.planType === 'monthly' && (
              <div className="space-y-3">
                <div className="bg-gray-50 p-3 rounded-md">
                  <p className="text-sm text-gray-700 font-medium mb-2">
                    Plan Limits (current values are pre-filled)
                  </p>
                  <div className="space-y-3">
                    {planForm.planType === 'monthly' && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Monthly Limit ($)
                          {currentMonthlyAllowance !== undefined ? (
                            <span className="text-xs text-gray-500 ml-1">
                              (Current: ${currentMonthlyAllowance}
                              {billingConfig.config && `, Default: $${billingConfig.config.default_monthly_allowance}`})
                            </span>
                          ) : billingConfig.config && (
                            <span className="text-xs text-gray-500 ml-1">
                              (Default: ${billingConfig.config.default_monthly_allowance})
                            </span>
                          )}
                        </label>
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          max="10000"
                          value={planForm.monthlyLimit}
                          onChange={(e) => setPlanForm(prev => ({ ...prev, monthlyLimit: e.target.value }))}
                          className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                          placeholder={currentMonthlyAllowance !== undefined ? currentMonthlyAllowance.toString() : (billingConfig.config ? billingConfig.config.default_monthly_allowance.toString() : "50.00")}
                        />
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
            
            <div className="bg-blue-50 p-3 rounded-md">
              <p className="text-sm text-blue-800">
                <strong>Current Plan:</strong> {getPlanInfo(currentPlan).label}
              </p>
              <p className="text-sm text-blue-700 mt-1">
                <strong>New Plan:</strong> {getPlanInfo(planForm.planType).label}
              </p>
              {currentWeeklyAllowance && (
                <p className="text-sm text-blue-600 mt-1">
                  <strong>Current Weekly Allowance:</strong> ${currentWeeklyAllowance}
                </p>
              )}
              {currentMonthlyAllowance && (
                <p className="text-sm text-blue-600 mt-1">
                  <strong>Current Monthly Allowance:</strong> ${currentMonthlyAllowance}
                </p>
              )}
              <p className="text-sm text-blue-600 mt-2">
                Changing the plan will update allowances but preserve existing credits.
              </p>
              {planForm.planType === 'monthly' && (
                <p className="text-sm text-blue-500 mt-1 font-medium">
                  💡 Current limit values are pre-filled - modify as needed or keep as-is.
                </p>
              )}
            </div>

            {/* Plan Comparison */}
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <h5 className="font-medium text-gray-700 mb-2">Current Plan Features</h5>
                <div className="space-y-1 text-gray-600">
                  <p>Plan: {getPlanInfo(currentPlan).label}</p>
                  <p>Type: {getPlanInfo(currentPlan).description}</p>
                  {currentPlan === 'unlimited' && (
                    <p className="text-purple-600 font-medium">Unlimited Access</p>
                  )}
                </div>
              </div>
              <div>
                <h5 className="font-medium text-gray-700 mb-2">New Plan Features</h5>
                <div className="space-y-1 text-gray-600">
                  <p>Plan: {getPlanInfo(planForm.planType).label}</p>
                  <p>Type: {getPlanInfo(planForm.planType).description}</p>
                  {planForm.planType === 'unlimited' && (
                    <p className="text-purple-600 font-medium">Unlimited Access</p>
                  )}
                </div>
              </div>
            </div>
            
            {operations.error && (
              <div className="text-sm text-red-600 bg-red-50 p-2 rounded">
                {operations.error}
              </div>
            )}
            
            <div className="flex justify-end space-x-3 pt-4">
              <button
                type="button"
                onClick={handleClose}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={operations.loading || planForm.planType === currentPlan}
                className="px-4 py-2 text-sm font-medium text-white bg-purple-600 rounded-md hover:bg-purple-700 disabled:opacity-50"
              >
                {operations.loading ? 'Changing...' : 
                 planForm.planType === currentPlan ? 'Same Plan' : 'Change Plan'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ChangePlanModal;
